import React, { useState, useEffect, useCallback } from 'react';
import './leaderboard.css';

function Ideas() {
  const [ideasData, setIdeasData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [flipAnimations, setFlipAnimations] = useState({});
  const REFRESH_INTERVAL = 30000; // 30 secondi

  const fetchIdeas = useCallback(async (isAutoRefresh = false) => {
    if (!isAutoRefresh) {
      setIsLoading(true);
    }
    setError(null);

    const today = new Date().toISOString().split('T')[0];
    const url = `https://rc9qwy4d45.execute-api.eu-west-1.amazonaws.com/prod/sessions/${today}/submissions`;
    
    console.log('🌐 Calling API URL:', url);

    try {
      const response = await fetch(url, {
        headers: {
          'x-api-key': 'tedyTU5NY16dRz05Ff17V3IhcpsUzJcG26mG6aq4'
        }
      });

      if (!response.ok) {
        throw new Error('Errore nel caricamento delle idee');
      }

      const data = await response.json();
      console.log('📊 Ideas API Response:', data);
      
      // Transform submissions to ideas format
      const processedSubmissions = data.submissions?.map(submission => {
        // Calculate total score from evaluations
        const evaluations = submission.evaluations || {};
        const votes = Object.values(evaluations)
          .filter(ev => ev && ev.vote !== null && ev.vote !== undefined)
          .map(ev => ev.vote);
        
        const totalScore = votes.length > 0 
          ? votes.reduce((sum, vote) => sum + vote, 0) / votes.length 
          : 0;
        
        return {
          ...submission,
          judges: evaluations, // Rename for compatibility
          totalScore
        };
      }) || [];
      
      // Sort by timestamp descending (most recent first) and add sequential number
      const sortedSubmissions = processedSubmissions
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
        .map((submission, index) => ({
          ...submission,
          rank: index + 1
        }));
      
      const transformedData = {
        contestSessionId: data.contestSessionId,
        totalEntries: data.totalSubmissions,
        leaderboard: sortedSubmissions
      };
      
      console.log('📋 Transformed ideas:', transformedData);
      
      // Detect changes and trigger flip animations
      setIdeasData((prevData) => {
        if (isAutoRefresh && prevData) {
          const newFlips = {};
          transformedData.leaderboard?.forEach((newEntry, index) => {
            const oldEntry = prevData.leaderboard?.[index];
            if (oldEntry && oldEntry.submissionId !== newEntry.submissionId) {
              newFlips[newEntry.submissionId] = true;
            }
          });
          if (Object.keys(newFlips).length > 0) {
            setFlipAnimations(newFlips);
            setTimeout(() => setFlipAnimations({}), 1000);
          }
        }
        return transformedData;
      });
      
      setLastUpdate(new Date());
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchIdeas();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchIdeas(true);
    }, REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, [fetchIdeas, REFRESH_INTERVAL]);

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateText = (text, maxLength = 25) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const formatLastUpdate = () => {
    if (!lastUpdate) return '';
    return lastUpdate.toLocaleTimeString('it-IT', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getRankBadgeClass = () => {
    return 'bg-info';
  };

  if (isLoading) {
    return (
      <div className="app-container">
        <div className="text-center text-white">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Caricamento...</span>
          </div>
          <p className="mt-3">Caricamento idee...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="app-container">
        <div className="form-card">
          <div className="form-body text-center">
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
            <button className="btn btn-primary" onClick={fetchIdeas}>
              Riprova
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundImage: `url(${process.env.PUBLIC_URL}/sfondo.jpg)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          zIndex: -1
        }}
      />
      <div className="leaderboard-container">
        <div className="leaderboard-card">
          <div className="leaderboard-header">
            <img 
              src={`${process.env.PUBLIC_URL}/logoTabata.png`} 
              alt="Tabata" 
              className="logo-tabata"
            />
            <h2 className="mb-0 mt-3">Tutte le Idee</h2>
            <p className="mb-0 mt-2 header-info">
              Sessione: {ideasData?.contestSessionId}
            </p>
            <p className="mb-0 header-info">
              {ideasData?.totalEntries} idee inviate
            </p>
          </div>

        <div className="leaderboard-body">
          <div className="d-flex justify-content-end align-items-center mb-4">
            <div className="d-flex align-items-center gap-3">
              <div className="live-indicator">
                <span className="live-dot"></span>
                <span className="live-text">LIVE</span>
              </div>
              {lastUpdate && (
                <small className="text-muted">
                  Ultimo aggiornamento: {formatLastUpdate()}
                </small>
              )}
            </div>
          </div>

          {ideasData?.leaderboard?.length === 0 ? (
            <div className="text-center py-5">
              <p className="text-muted">Nessuna idea ancora inviata</p>
            </div>
          ) : (
            <div className="leaderboard-list">
              {ideasData?.leaderboard?.map((entry) => (
                <div 
                  key={entry.submissionId} 
                  className={`leaderboard-item ${flipAnimations[entry.submissionId] ? 'flip-animation' : ''}`}
                >
                  <div className="d-flex align-items-start gap-3">
                    <div className="rank-badge-container">
                      <span className={`rank-badge ${getRankBadgeClass()}`}>
                        #{entry.rank}
                      </span>
                    </div>

                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h5 className="mb-0">{entry.email}</h5>
                        <small className="text-muted">
                          {formatDate(entry.timestamp)}
                        </small>
                      </div>

                      <p className="idea-text mb-0" title={entry.ideaText}>
                        {truncateText(entry.ideaText, 20)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
}

export default Ideas;
